// apps/client/src/ui/screens/door.tsx (M1-T16) — your house's door setting (spec §9.9): open to residents (default), only when you're
// home, or locked. Three radio pills bound to your ResidentPublic.door; a pick sends `house.door` at once (optimistic, undone on a reject).
import type { StringKey } from '@whiskerton/content';
import type { DoorSetting, ResidentId } from '@whiskerton/shared';
import { useShallow } from 'zustand/react/shallow';
import { useCommands } from '../../game/instance';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Panel } from '../kit';
import type { ScreenDef } from '../router';

declare module '../router' { interface ScreenParams { door: undefined } }

const SETTINGS: readonly { id: DoorSetting; label: StringKey }[] = [
  { id: 'open', label: 'house.doorOpen' }, { id: 'home', label: 'house.doorHome' }, { id: 'locked', label: 'house.doorLocked' },
];
/** Shows `door` on your resident record now; returns the undo the action client runs on a reject. */
function showDoor(me: ResidentId, door: DoorSetting): () => void {
  const prev = useGame.getState().residents[me]?.door;
  if (!prev) return () => {};
  const put = (d: DoorSetting) => useGame.setState((s) => { const r = s.residents[me]; return r ? { residents: { ...s.residents, [me]: { ...r, door: d } } } : {}; });
  put(door);
  return () => put(prev);
}

function Door() {
  const t = useT();
  const cmd = useCommands();
  const { me, door } = useGame(useShallow((s) => ({ me: s.you?.resident ?? null, door: s.you ? (s.residents[s.you.resident]?.door ?? null) : null })));
  const pick = (d: DoorSetting) => {
    if (!me || d === door) return;
    void cmd.act('house.door', { door: d }, { optimistic: () => showDoor(me, d) });
  };
  return (
    <Panel title={t('decor.doorTitle')}>
      <div style={{ display: 'grid', gap: 14, width: 360, maxWidth: '100%' }}>
        <p style={{ margin: 0, fontWeight: 800 }}>{t('decor.doorNote')}</p>
        <div className="tabs" role="radiogroup" aria-label={t('decor.doorTitle')}>
          {SETTINGS.map((s) => (
            <button key={s.id} type="button" role="radio" className="tab" aria-checked={door === s.id} onClick={() => pick(s.id)}
              style={door === s.id ? { background: 'var(--leaf-700)', color: '#fff' } : undefined}>
              {t(s.label)}
            </button>
          ))}
        </div>
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'door'> = {
  id: 'door', layer: 'panel', component: () => <Door />, menu: { label: 'decor.doorTitle', order: 21, visible: (s) => !!s.you },
};
